import { FC, ReactNode } from 'react';
import ReactDatePicker, { ReactDatePickerProps } from 'react-datepicker';
import style from './DatePicker.module.scss';
import 'react-datepicker/dist/react-datepicker.css';
import { DropdownMenu, Icon, IconButton, Tooltip } from '@kontentino/ui';
import clsx from 'clsx';
import DatePickerPlaceholder from './DatepickerPlaceholder';
import { useUser } from 'modules/user/userSelector';
import { faX } from '@fortawesome/pro-regular-svg-icons/faX';

type Props = ReactDatePickerProps<boolean> & {
  isDisabled?: boolean;
  isSelected?: boolean;
  hideDefaultDatePicker?: boolean;
  label: string;
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  refreshKey?: string;
  placeholderClassname?: string;
  contentClassname?: string;
  contentAlign?: 'start' | 'center' | 'end';
  dataCy?: {
    day?: string;
    placeholder?: string;
  };
  clearButtonTooltipContent?: string | ReactNode;
  onClear?: () => void;
  modal?: boolean;
  topContent?: ReactNode;
};

const DatePicker: FC<Props> = ({
  isOpen,
  onOpenChange,
  isDisabled,
  isSelected,
  hideDefaultDatePicker,
  label,
  refreshKey,
  children,
  placeholderClassname,
  dataCy,
  contentClassname,
  clearButtonTooltipContent,
  contentAlign,
  onClear,
  modal,
  topContent,
  ...props
}) => {
  const user = useUser();

  return (
    <DropdownMenu.Root open={isOpen} onOpenChange={onOpenChange} modal={modal}>
      <div className="tw-relative">
        <DropdownMenu.Trigger disabled={isDisabled}>
          <div>
            <DatePickerPlaceholder
              label={label}
              isOpen={isOpen}
              isSelected={isSelected}
              isDisabled={isDisabled}
              className={clsx('tw-relative tw-w-full', placeholderClassname)}
              dataCy={dataCy?.placeholder}
            />
          </div>
        </DropdownMenu.Trigger>
        {onClear && props.selected && (
          <div className="tw-absolute tw-right-10 tw-top-1/2 -tw-translate-y-1/2">
            <Tooltip content={clearButtonTooltipContent}>
              <IconButton
                icon={
                  <Icon
                    icon={faX}
                    size={'sm'}
                    className="tw-text-grayscale-100"
                  />
                }
                size="small"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onClear();
                }}
                variant="ghost"
                data-cy="clear-button"
              />
            </Tooltip>
          </div>
        )}
      </div>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          className={clsx(
            style.datePicker,
            'tw-top-1 !tw-z-[999999] tw-gap-y-0 tw-p-0',
            { [style.multiMonth]: (props.monthsShown ?? 1) > 1 },
            contentClassname,
          )}
          data-cy="datepicker-content"
          align={contentAlign}
        >
          {topContent}
          {!hideDefaultDatePicker && (
            <ReactDatePicker
              {...props}
              key={refreshKey}
              inline
              isClearable={true}
              customInput={<></>}
              renderDayContents={(day) => (
                <span data-cy={dataCy?.day} className="day">
                  {day}
                </span>
              )}
              calendarStartDay={user.useMeridianTime ? 1 : 0}
            />
          )}
          {children}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
};

export default DatePicker;
